const SCRATCH_LEAD_KEY = 'eantas_scratch_lead';

const scratchGetLead = () => {
  try {
    return JSON.parse(localStorage.getItem(SCRATCH_LEAD_KEY)) || {};
  } catch (error) {
    return {};
  }
};

const scratchStoreLead = (patch) => {
  const state = Object.assign(scratchGetLead(), patch, { updatedAt: Date.now() });
  try {
    localStorage.setItem(SCRATCH_LEAD_KEY, JSON.stringify(state));
  } catch (error) {
    /* ignore */
  }
  return state;
};

if (!customElements.get('scratch-card')) {
  class ScratchCard extends HTMLElement {
    constructor() {
      super();
      this.drawing = false;
      this.revealed = false;
      this.lastX = null;
      this.lastY = null;
      this.lastCheck = 0;
      this.scratchPath = 0;
      this.resizeObserver = null;
    }

    connectedCallback() {
      this.area = this.querySelector('.scratch-card__area');
      this.canvas = this.querySelector('.scratch-card__foil');
      this.progressEl = this.querySelector('[data-scratch-progress]');
      this.percentEl = this.querySelector('[data-scratch-percent]');
      this.successEl = this.querySelector('[data-scratch-success]');
      this.popBadge = this.querySelector('[data-scratch-pop-badge]');
      this.confettiEl = this.querySelector('[data-scratch-confetti]');
      this.isPopup = this.classList.contains('scratch-card--popup');
      if (!this.area || !this.canvas) return;

      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      this.readConfig();

      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerEnd = this.handlePointerEnd.bind(this);
      this.onTouchMove = (evt) => evt.preventDefault();
      this.onResize = () => {
        if (!this.revealed) this.paintFoil();
      };

      this.canvas.addEventListener('pointerdown', this.onPointerDown);
      this.canvas.addEventListener('pointermove', this.onPointerMove);
      this.canvas.addEventListener('pointerup', this.onPointerEnd);
      this.canvas.addEventListener('pointercancel', this.onPointerEnd);
      this.canvas.addEventListener('pointerleave', this.onPointerEnd);
      this.canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.area);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      if (Shopify.designMode) {
        document.addEventListener('shopify:section:load', (evt) => {
          if (evt.target.contains(this)) {
            this.revealed = false;
            this.resetState();
            this.paintFoil();
          }
        });
      }

      this.paintFoil();
      this.setupEmailForm();
    }

    disconnectedCallback() {
      if (!this.canvas) return;

      this.canvas.removeEventListener('pointerdown', this.onPointerDown);
      this.canvas.removeEventListener('pointermove', this.onPointerMove);
      this.canvas.removeEventListener('pointerup', this.onPointerEnd);
      this.canvas.removeEventListener('pointercancel', this.onPointerEnd);
      this.canvas.removeEventListener('pointerleave', this.onPointerEnd);
      this.canvas.removeEventListener('touchmove', this.onTouchMove);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.onResize);
      }
    }

    readConfig() {
      this.brush = Number(this.dataset.brush) || 30;
      this.brushMobile = Number(this.dataset.brushMobile) || Math.round(this.brush * 1.45);
      this.threshold = Number(this.dataset.threshold) || 0.46;
      this.revealHoldMs = Number(this.dataset.revealHoldMs) || 1600;
      this.emailStepDelay = Number(this.dataset.emailStepDelay) || 900;
      this.discountCode = (this.dataset.discountCode || '').trim();
      this.emailSuccess = (this.dataset.emailSuccess || '').trim();
      this.klaviyoEvent = (this.dataset.klaviyoEvent || '').trim();
      this.klaviyoCompany = (this.dataset.klaviyoCompany || '').trim();
      this.klaviyoList = (this.dataset.klaviyoList || '').trim();
      this.klaviyoSource = (this.dataset.klaviyoSource || 'eantas-scratch-popup').trim();
      this.emailTags = (this.dataset.emailTags || 'prospect,newsletter,rubbellos').trim();
      this.showProgress = this.dataset.showProgress === 'true';
      this.revealSuccess = (this.dataset.revealSuccess || 'Erfolgreich!').trim();
      this.thresholdPercent = Number(this.dataset.thresholdPercent) || Math.round(this.threshold * 100);
      this.minScratchPath = this.isPopup ? 140 : 80;
      this.foilColors = [
        this.dataset.foilColor1 || '#F2E6CC',
        this.dataset.foilColor2 || '#CDB694',
        this.dataset.foilColor3 || '#BCA37C'
      ];
      this.useTexture = this.dataset.foilTexture === 'true';
      this.useSheen = this.dataset.foilSheen === 'true';
    }

    getDpr() {
      return Math.min(window.devicePixelRatio || 1, 2);
    }

    getBrushSize() {
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      const narrow = window.matchMedia('(max-width: 767.98px)').matches;
      return coarse || narrow ? this.brushMobile : this.brush;
    }

    setupEmailForm() {
      const popupForm = this.querySelector('[data-scratch-email-form]');
      if (popupForm && popupForm.dataset.ajaxBound !== 'true') {
        popupForm.dataset.ajaxBound = 'true';
        popupForm.addEventListener('submit', (evt) => this.handlePopupEmailSubmit(evt, popupForm));
      }

      const wrapper = this.querySelector('.scratch-card__form');
      const form = wrapper?.querySelector('form');
      if (!form || form.dataset.ajaxBound === 'true') return;

      form.dataset.ajaxBound = 'true';
      form.addEventListener('submit', (evt) => this.handleEmailSubmit(evt, form, wrapper));
    }

    showStep(name) {
      this.querySelectorAll('.scratch-card__step').forEach((step) => {
        const active = step.dataset.step === name;
        step.classList.toggle('is-active', active);
        step.hidden = !active;
        if (active) {
          step.classList.remove('is-entering');
          window.requestAnimationFrame(() => step.classList.add('is-entering'));
        }
      });

      const focusTarget = this.querySelector(
        `.scratch-card__step[data-step="${name}"] input:not([type="checkbox"]), .scratch-card__step[data-step="${name}"] button[type="submit"]`
      );
      if (focusTarget) {
        window.setTimeout(() => {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch (error) {
            focusTarget.focus();
          }
        }, 70);
      }
    }

    subscribeKlaviyo(email) {
      if (!this.klaviyoCompany || !this.klaviyoList) {
        return Promise.resolve({ skipped: true });
      }

      return fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.klaviyoCompany)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties: {
                        eantas_welcome_code: this.discountCode,
                        eantas_source: this.klaviyoSource
                      }
                    }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: this.klaviyoList } } }
            }
          })
        }
      ).then((response) => {
        if (!response.ok && response.status !== 202) throw new Error(`klaviyo ${response.status}`);
        return response;
      });
    }

    subscribeShopify(email) {
      const formData = new FormData();
      formData.set('form_type', 'customer');
      formData.set('utf8', '✓');
      formData.set('contact[email]', email);
      formData.set('contact[tags]', this.emailTags);

      return fetch('/contact', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html,application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        }
      }).then((response) => {
        if (!response.ok) throw new Error('shopify_subscribe_failed');
        return response;
      });
    }

    async handlePopupEmailSubmit(evt, form) {
      evt.preventDefault();

      const emailInput = form.querySelector('[name="email"]');
      const consent = form.querySelector('[data-scratch-consent]');
      const errorEl = this.querySelector('[data-scratch-form-error]');
      const submitBtn = form.querySelector('[type="submit"]');
      const email = (emailInput?.value || '').trim();

      if (errorEl) errorEl.textContent = '';

      if (!consent || !consent.checked) {
        if (errorEl) errorEl.textContent = 'Bitte bestätige die Einwilligung.';
        if (consent) consent.focus();
        return;
      }

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (errorEl) errorEl.textContent = 'Bitte prüfe deine E-Mail-Adresse.';
        if (emailInput) emailInput.focus();
        return;
      }

      if (submitBtn) submitBtn.disabled = true;

      try {
        await this.subscribeShopify(email);
        try {
          await this.subscribeKlaviyo(email);
        } catch (klaviyoError) {
          scratchStoreLead({ pendingEmail: email });
        }

        this.trackKlaviyo(email);
        scratchStoreLead({ subscribed: true, source: this.klaviyoSource });
        this.activateDiscount();

        const step = form.closest('.scratch-card__step');
        if (step) {
          step.replaceChildren();
          const response = document.createElement('div');
          response.className = 'signup-form__response signup-form__response--success';
          const paragraph = document.createElement('p');
          paragraph.textContent = this.emailSuccess || 'Danke! Dein Rabatt ist aktiv.';
          response.appendChild(paragraph);
          step.appendChild(response);
        }
      } catch (error) {
        if (errorEl) errorEl.textContent = 'Das hat nicht geklappt. Bitte versuche es erneut.';
        if (submitBtn) submitBtn.disabled = false;
        if (emailInput) emailInput.focus();
      }
    }

    activateDiscount() {
      if (!this.discountCode) return;
      fetch(`/discount/${encodeURIComponent(this.discountCode)}`, { credentials: 'same-origin' }).catch(() => {});
    }

    async handleEmailSubmit(evt, form, wrapper) {
      evt.preventDefault();

      const emailInput = form.querySelector('[name="contact[email]"]');
      const submitBtn = form.querySelector('[type="submit"]');
      const email = (emailInput?.value || '').trim();
      if (!email || !emailInput) return;

      wrapper.classList.add('is-submitting');
      if (submitBtn) submitBtn.disabled = true;

      const formData = new FormData(form);
      if (!formData.get('form_type')) {
        formData.set('form_type', 'customer');
      }

      const contactUrl = '/contact';

      try {
        const response = await fetch(contactUrl, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html,application/xhtml+xml',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error('subscribe_failed');
        }

        this.showEmailSuccess(form, wrapper);
        this.trackKlaviyo(email);
      } catch (error) {
        if (submitBtn) submitBtn.disabled = false;
        wrapper.classList.remove('is-submitting');
        emailInput.focus();
      }
    }

    showEmailSuccess(form, wrapper) {
      const message = this.emailSuccess || 'Danke! Dein Rabatt ist aktiv.';
      wrapper.replaceChildren();
      const response = document.createElement('div');
      response.className = 'signup-form__response signup-form__response--success';
      const paragraph = document.createElement('p');
      paragraph.textContent = message;
      response.appendChild(paragraph);
      wrapper.appendChild(response);
      wrapper.classList.remove('is-submitting');
    }

    trackKlaviyo(email) {
      const learnq = window._learnq;
      if (!learnq || typeof learnq.push !== 'function') return;

      learnq.push(['identify', { $email: email }]);

      if (this.klaviyoEvent) {
        learnq.push(['track', this.klaviyoEvent, { source: 'scratch-card' }]);
      }
    }

    repaintFoil() {
      if (!this.revealed) this.paintFoil();
    }

    resetState() {
      this.drawing = false;
      this.lastX = null;
      this.lastY = null;
      this.lastCheck = 0;
      this.scratchPath = 0;
      this.classList.remove('is-revealed', 'is-scratching');
      this.canvas.style.transition = '';
      this.canvas.style.opacity = '';
      this.canvas.style.pointerEvents = '';

      if (this.progressEl) this.progressEl.hidden = true;
      if (this.successEl) {
        this.successEl.hidden = true;
        this.successEl.textContent = '';
      }
      if (this.popBadge) {
        this.popBadge.hidden = true;
        this.popBadge.classList.remove('is-on');
      }
      if (this.isPopup) this.showStep('scratch');

      const after = this.querySelector('.scratch-card__after');
      if (after) after.classList.remove('is-visible');
    }

    paintFoil() {
      const rect = this.area.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const dpr = this.getDpr();
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.ctx.globalCompositeOperation = 'source-over';

      const w = rect.width;
      const h = rect.height;
      const radius = Number(getComputedStyle(this.area).borderRadius.replace('px', '')) || 16;

      this.ctx.beginPath();
      this.roundRectPath(0, 0, w, h, radius);
      this.ctx.clip();

      const gradient = this.ctx.createLinearGradient(0, 0, w, h);
      gradient.addColorStop(0, this.foilColors[0]);
      gradient.addColorStop(0.55, this.foilColors[1]);
      gradient.addColorStop(1, this.foilColors[2]);
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, w, h);

      if (this.useTexture) {
        this.ctx.save();
        this.ctx.globalAlpha = 0.08;
        this.ctx.strokeStyle = '#000';
        this.ctx.lineWidth = 1;
        const step = 10;
        for (let x = -h; x < w + h; x += step) {
          this.ctx.beginPath();
          this.ctx.moveTo(x, 0);
          this.ctx.lineTo(x + h, h);
          this.ctx.stroke();
        }
        this.ctx.restore();
      }

      if (this.useSheen) {
        this.ctx.save();
        const sheen = this.ctx.createLinearGradient(0, 0, w, 0);
        sheen.addColorStop(0, 'rgba(255,255,255,0)');
        sheen.addColorStop(0.45, 'rgba(255,255,255,0.35)');
        sheen.addColorStop(0.55, 'rgba(255,255,255,0.15)');
        sheen.addColorStop(1, 'rgba(255,255,255,0)');
        this.ctx.fillStyle = sheen;
        this.ctx.fillRect(0, 0, w, h);
        this.ctx.restore();
      }
    }

    roundRectPath(x, y, width, height, radius) {
      const r = Math.min(radius, width / 2, height / 2);
      this.ctx.moveTo(x + r, y);
      this.ctx.arcTo(x + width, y, x + width, y + height, r);
      this.ctx.arcTo(x + width, y + height, x, y + height, r);
      this.ctx.arcTo(x, y + height, x, y, r);
      this.ctx.arcTo(x, y, x + width, y, r);
      this.ctx.closePath();
    }

    scratchAt(clientX, clientY) {
      const rect = this.canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const brush = this.getBrushSize();

      this.ctx.globalCompositeOperation = 'destination-out';

      const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, brush);
      gradient.addColorStop(0, 'rgba(0,0,0,1)');
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(x, y, brush, 0, Math.PI * 2);
      this.ctx.fill();

      if (this.lastX !== null && this.lastY !== null) {
        this.scratchPath += Math.hypot(x - this.lastX, y - this.lastY);
        this.ctx.lineWidth = brush * 2;
        this.ctx.lineCap = 'round';
        this.ctx.strokeStyle = 'rgba(0,0,0,1)';
        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);
        this.ctx.lineTo(x, y);
        this.ctx.stroke();
      }

      this.lastX = x;
      this.lastY = y;
    }

    clearedRatio() {
      const dpr = this.getDpr();
      const data = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
      let cleared = 0;
      let total = 0;
      const stride = Math.max(16, 4 * dpr * 4);

      for (let i = 3; i < data.length; i += stride) {
        total += 1;
        if (data[i] < 96) cleared += 1;
      }

      return total ? cleared / total : 0;
    }

    shouldReveal(ratio) {
      if (ratio < this.threshold) return false;
      return this.scratchPath >= this.minScratchPath;
    }

    burstConfetti() {
      const host = this.area || this.confettiEl;
      if (!host) return;

      const rect = host.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const colors = ['#F2E6CC', '#CDB694', '#E8A87C', '#ffffff', '#BCA37C', '#D4AF37'];
      const root = document.body;

      for (let i = 0; i < 56; i += 1) {
        const piece = document.createElement('span');
        piece.className = 'scratch-card__confetti-piece';
        piece.style.background = colors[i % colors.length];
        piece.style.left = `${cx}px`;
        piece.style.top = `${cy}px`;

        const angle = Math.random() * Math.PI * 2;
        const distance = 60 + Math.random() * 140;
        piece.style.setProperty('--tx', `${Math.cos(angle) * distance}px`);
        piece.style.setProperty('--ty', `${Math.sin(angle) * distance}px`);
        piece.style.setProperty('--rot', `${Math.random() * 720 - 360}deg`);
        piece.style.animationDuration = `${1.4 + Math.random() * 0.9}s`;

        root.appendChild(piece);
        window.setTimeout(() => piece.remove(), 2400);
      }
    }

    getClearedPercent() {
      return Math.min(100, Math.round(this.clearedRatio() * 100));
    }

    updateProgressUI() {
      if (!this.showProgress || this.revealed) return;

      const percent = this.getClearedPercent();
      if (this.progressEl) this.progressEl.hidden = false;
      if (this.percentEl) this.percentEl.textContent = `${percent}%`;
    }

    maybeReveal() {
      const ratio = this.clearedRatio();
      if (this.revealed || !this.shouldReveal(ratio)) {
        if (this.showProgress) this.updateProgressUI();
        return;
      }

      const percent = Math.min(100, Math.round(ratio * 100));
      this.revealed = true;
      this.drawing = false;
      this.lastX = null;
      this.lastY = null;
      this.classList.add('is-revealed');
      this.classList.remove('is-scratching');

      if (this.progressEl) this.progressEl.hidden = true;

      if (this.isPopup) {
        if (this.popBadge) {
          this.popBadge.hidden = false;
          this.popBadge.classList.add('is-on');
        }
      } else if (this.successEl) {
        this.successEl.hidden = false;
        this.successEl.textContent = `${this.revealSuccess} (${percent}%)`;
      }

      this.canvas.style.transition = 'opacity 0.75s ease';
      this.canvas.style.opacity = '0';
      this.canvas.style.pointerEvents = 'none';

      this.burstConfetti();
      this.activateDiscount();

      this.dispatchEvent(new CustomEvent('scratch:revealed', { bubbles: true, detail: { percent } }));

      if (this.isPopup) {
        const badgeHold = this.revealHoldMs > 0 ? this.revealHoldMs : 2200;
        const emailDelay = this.emailStepDelay > 0 ? this.emailStepDelay : 900;
        window.setTimeout(() => {
          if (this.popBadge) this.popBadge.classList.remove('is-on');
        }, badgeHold);
        window.setTimeout(() => {
          this.showStep('email');
        }, badgeHold + emailDelay);
        return;
      }

      const after = this.querySelector('.scratch-card__after');
      if (after) {
        const delay = this.revealHoldMs > 0 ? this.revealHoldMs : 0;
        window.setTimeout(() => {
          after.classList.add('is-visible');
          const emailInput = after.querySelector('[name="contact[email]"]');
          if (emailInput) window.setTimeout(() => emailInput.focus(), 80);
        }, delay);
      }
    }

    handlePointerDown(evt) {
      if (this.revealed) return;
      this.drawing = true;
      this.lastX = null;
      this.lastY = null;
      this.scratchPath = 0;
      this.classList.add('is-scratching');
      this.canvas.setPointerCapture(evt.pointerId);
      this.scratchAt(evt.clientX, evt.clientY);
      this.updateProgressUI();
    }

    handlePointerMove(evt) {
      if (!this.drawing || this.revealed) return;
      this.scratchAt(evt.clientX, evt.clientY);

      if (Date.now() - this.lastCheck > 100) {
        this.lastCheck = Date.now();
        this.maybeReveal();
      } else if (this.showProgress) {
        this.updateProgressUI();
      }
    }

    handlePointerEnd() {
      if (!this.drawing) return;
      this.drawing = false;
      this.lastX = null;
      this.lastY = null;
      this.classList.remove('is-scratching');
      this.maybeReveal();
    }
  }

  customElements.define('scratch-card', ScratchCard);
}

if (!customElements.get('scratch-card-popup')) {
  /* global Modal */

  const SESSION_START_KEY = 'eantas_scratch_start';

  const getCookie = (name) => {
    const cookieString = `; ${document.cookie}`;
    const parts = cookieString.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const setCookie = (name, value, days) => {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
  };

  // Zeit seit dem ersten Seitenaufruf der Sitzung, damit der Timer beim Seitenwechsel nicht neu startet
  const getSessionElapsed = () => {
    try {
      const saved = Number(sessionStorage.getItem(SESSION_START_KEY));
      if (saved) return Date.now() - saved;
      sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    } catch (error) {
      /* ignore */
    }
    return 0;
  };

  customElements.whenDefined('modal-dialog').then(() => {
    class ScratchCardPopup extends Modal {
      static getLead() {
        return scratchGetLead();
      }

      static storeLead(patch) {
        return scratchStoreLead(patch);
      }

      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;
        this.scrollHandler = null;
        this.mouseLeaveHandler = null;
      }

      connectedCallback() {
        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) {
              this.open();
            }
          });
          document.addEventListener('shopify:section:deselect', () => this.close());
          return;
        }

        if (getCookie(this.cookie)) return;

        const trigger = this.dataset.trigger || 'delay';
        if (trigger === 'delay') {
          const delay = Number(this.dataset.delay || 3) * 1000;
          const remaining = Math.max(0, delay - getSessionElapsed());
          window.setTimeout(() => this.openOnce(), remaining);
        } else if (trigger === 'scroll') {
          const threshold = Number(this.dataset.scrollPercent || 70) / 100;
          this.scrollHandler = () => {
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            if (maxScroll <= 0) return;
            const scrolled = window.scrollY / maxScroll;
            if (scrolled >= threshold) {
              this.openOnce();
              window.removeEventListener('scroll', this.scrollHandler);
            }
          };
          window.addEventListener('scroll', this.scrollHandler, { passive: true });
        } else if (trigger === 'exit' && theme.mediaMatches?.md) {
          this.mouseLeaveHandler = (evt) => {
            if (evt.clientY < 0) this.openOnce();
          };
          document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
        }
      }

      disconnectedCallback() {
        if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
        if (this.mouseLeaveHandler) document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
      }

      openOnce() {
        if (getCookie(this.cookie)) return;
        if (ScratchCardPopup.getLead().subscribed) return;
        if (window.location.pathname.startsWith('/cart') || window.location.pathname.startsWith('/checkout')) return;
        this.open();
      }

      open(opener) {
        super.open(opener);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.querySelectorAll('scratch-card').forEach((card) => {
              if (typeof card.repaintFoil === 'function') card.repaintFoil();
            });
          });
        });
      }

      close() {
        super.close();
        const days = Number(this.dataset.dismissDays || 7);
        setCookie(this.cookie, '1', days);
      }
    }

    customElements.define('scratch-card-popup', ScratchCardPopup);

    (function retryPendingKlaviyo() {
      const pending = ScratchCardPopup.getLead().pendingEmail;
      if (!pending) return;

      const card = document.querySelector('scratch-card.scratch-card--popup');
      if (!card || !card.dataset.klaviyoCompany || !card.dataset.klaviyoList) return;

      card.subscribeKlaviyo(pending)
        .then((result) => {
          if (!result?.skipped) ScratchCardPopup.storeLead({ pendingEmail: null });
        })
        .catch(() => {});
    })();
  });
}
